import {
    cancelSignOffEnvelope,
    createSignOffComment,
    createSignOffEnvelope,
    deleteSignOffComment,
    deleteSignOffEnvelope,
    getSignOffEnvelope,
    getSignOffEnvelopes,
    getSignOffTemplateToken,
    processSignOffDocumentEvent,
    processSignOffDocumentSignature,
    processSignOffEnvelopeEvent,
    updateSignOffComment,
    updateSignOffDocumentSigner,
    updateSignOffDocumentSignatures,
    updateSignOffEnvelope,
    updateSignOffEnvelopeApprovers,
    updateSignOffEnvelopeInternalApprovers,
} from '@/api/sign-off-envelope.api';
import DocusealModal from '@/components/sign-off/DocusealModal.vue';
import DocusealSignerModal from '@/components/sign-off/DocusealSignerModal.vue';
import { showModal } from '@/shared';
import type {
    CreateSignOffEnvelopeRequest,
    DocumentSignatureStatus,
    ICreateSignOffCommentRequest,
    ISignOffEnvelopeFilter,
    ISignOffFormBuilderRequest,
    IUpdateSignOffCommentParams,
    SignOffEnvelopeExpandOpts,
    UpdateSignOffEnvelopeParams,
    UpdateSignOffEnvelopeInternalApproversParams,
    UpdateSignOffDocumentSignerParams,
} from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, unref } from 'vue';

export const useSignOffEnvelopes = (params?: MaybeRef<ISignOffEnvelopeFilter>, expand?: SignOffEnvelopeExpandOpts) => {
    return useQuery(['sign-off-envelopes', params, expand], () => getSignOffEnvelopes(unref(params), expand), {
        initialData: { signOffEnvelopes: [], total: 0 },
    });
};

export const useSignOffEnvelope = (signOffEnvelopeId: MaybeRef<number>, expand?: SignOffEnvelopeExpandOpts) => {
    return useQuery(['sign-off-envelope', signOffEnvelopeId, expand], () => getSignOffEnvelope(unref(signOffEnvelopeId), expand), {
        initialData: null,
    });
};

export const useSignOffEnvelopeMutations = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: createSignOffEnvelopeMutation } = useMutation(
        (signOffEnvelope: CreateSignOffEnvelopeRequest) => createSignOffEnvelope(signOffEnvelope),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
            },
        },
    );

    const { mutateAsync: cancelSignOffEnvelopeMutation } = useMutation(
        (signOffEnvelopeId: MaybeRef<number>) => cancelSignOffEnvelope(unref(signOffEnvelopeId)),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    const { mutateAsync: deleteSignOffEnvelopeMutation } = useMutation(
        (signOffEnvelopeId: MaybeRef<number>) => deleteSignOffEnvelope(unref(signOffEnvelopeId)),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
            },
        },
    );

    const { mutateAsync: updateSignOffEnvelopeMutation } = useMutation(
        (updateParams: MaybeRef<UpdateSignOffEnvelopeParams>) => updateSignOffEnvelope(unref(updateParams)),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    const { mutateAsync: updateSignOffDocumentSignaturesMutation } = useMutation(
        (params: {
            signOffDocumentId: number;
            signatures: Array<{
                isInternalSignature: boolean;
                contactId: number;
                userRole: string;
                userSigningOrder: number | null;
            }>;
            templateId?: number | null;
        }) => updateSignOffDocumentSignatures(params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    const { mutateAsync: updateSignOffDocumentSignerMutation } = useMutation(
        (params: UpdateSignOffDocumentSignerParams) => updateSignOffDocumentSigner(params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    const { mutateAsync: updateSignOffEnvelopeApproversMutation } = useMutation(
        (params: {
            signOffEnvelopeId: number;
            approvers: Array<{
                contactId: number;
            }>;
        }) => updateSignOffEnvelopeApprovers(params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    const { mutateAsync: updateSignOffEnvelopeInternalApproversMutation } = useMutation(
        (params: UpdateSignOffEnvelopeInternalApproversParams) => updateSignOffEnvelopeInternalApprovers(params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    const { mutateAsync: processSignOffEnvelopeEventMutation } = useMutation(
        (params: { signOffEnvelopeId: number; event: string }) => processSignOffEnvelopeEvent(params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    const { mutateAsync: processSignOffDocumentEventMutation } = useMutation(
        (params: { signOffDocumentId: number; event: string; rejectionReason?: string }) => processSignOffDocumentEvent(params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );
    const { mutateAsync: processSignOffDocumentSignatureMutation } = useMutation(
        (params: { signOffDocumentId: number; event: DocumentSignatureStatus; contactId: number }) => processSignOffDocumentSignature(params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelopes']);
                queryClient.invalidateQueries(['sign-off-envelope']);
            },
        },
    );

    return {
        createSignOffEnvelope: createSignOffEnvelopeMutation,
        cancelSignOffEnvelope: cancelSignOffEnvelopeMutation,
        deleteSignOffEnvelope: deleteSignOffEnvelopeMutation,
        updateSignOffEnvelope: updateSignOffEnvelopeMutation,
        updateSignOffDocumentSignatures: updateSignOffDocumentSignaturesMutation,
        updateSignOffDocumentSigner: updateSignOffDocumentSignerMutation,
        updateSignOffEnvelopeApprovers: updateSignOffEnvelopeApproversMutation,
        updateSignOffEnvelopeInternalApprovers: updateSignOffEnvelopeInternalApproversMutation,
        processSignOffEnvelopeEvent: processSignOffEnvelopeEventMutation,
        processSignOffDocumentEvent: processSignOffDocumentEventMutation,
        processSignOffDocumentSignature: processSignOffDocumentSignatureMutation,
    };
};

type DocusealTemplateResult = {
    id: number;
    submitters: { name: string; uuid: string }[];
    fields: {
        name: string;
        required: boolean;
        type: 'text' | 'signature' | 'date';
        submitter_uuid: string;
    }[];
};

export const useDocusealFormBuilder = () => {
    const startFormBuilder = async (params: ISignOffFormBuilderRequest['formBuilder']): Promise<{ data: DocusealTemplateResult | null; close: () => void }> => {
        const token = await getSignOffTemplateToken({ formBuilder: params });

        return new Promise(res => {
            const modal = showModal(DocusealModal, {
                props: {
                    token,
                },
                width: 1200,
                events: {
                    confirm: (data: DocusealTemplateResult) => {
                        res({
                            data,
                            close: () => modal.close(),
                        });
                    },
                    close: () => {
                        modal.close();
                    },
                },
            });
        });
    };

    return { startFormBuilder };
};

export const useDocusealSigner = () => {
    const openSigner = async (params: {
        src: string;
        email: string;
        onComplete?: () => void;
        onDecline?: () => void;
        onClose?: () => void;
    }): Promise<{ close: () => void }> => {
        return new Promise(res => {
            const modal = showModal(DocusealSignerModal, {
                props: {
                    src: params.src,
                    email: params.email,
                },
                width: 1200,
                events: {
                    complete: () => {
                        params.onComplete?.();
                        modal.close();
                    },
                    decline: () => {
                        params.onDecline?.();
                        modal.close();
                    },
                    close: () => {
                        params.onClose?.();
                        modal.close();
                    },
                },
            });

            res({
                close: () => modal.close(),
            });
        });
    };

    return { openSigner };
};

// Comment hooks
export const useSignOffCommentMutations = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: createCommentMutation } = useMutation((req: ICreateSignOffCommentRequest) => createSignOffComment(req), {
        onSuccess: () => {
            queryClient.invalidateQueries(['sign-off-envelope']);
            queryClient.invalidateQueries(['sign-off-envelopes']);
        },
    });

    const { mutateAsync: updateCommentMutation } = useMutation(
        ({ commentId, request }: { commentId: number; request: Partial<IUpdateSignOffCommentParams> }) => updateSignOffComment(commentId, request),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-envelope']);
                queryClient.invalidateQueries(['sign-off-envelopes']);
            },
        },
    );

    const { mutateAsync: deleteCommentMutation } = useMutation((commentId: number) => deleteSignOffComment(commentId), {
        onSuccess: () => {
            queryClient.invalidateQueries(['sign-off-envelope']);
            queryClient.invalidateQueries(['sign-off-envelopes']);
        },
    });

    return {
        createComment: createCommentMutation,
        updateComment: updateCommentMutation,
        deleteComment: deleteCommentMutation,
    };
};
